import { Flex, Box, Text, Loader, Group, useMantineTheme } from '@mantine/core';
import { useEffect, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { SuitabilitySelectableList } from '../edit/components/SuitabilitySelectableList';
import { UploadPillButton } from '../edit/components/UploadPillButton';
import { StepProps } from '../types';
import { SelectedSwitchRow } from '../edit/components/SelectedSwitchRow';
import { useSelectableUploadItems } from '../hooks/useSelectableUploadItems';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useAllClientDocuments, type UnifiedDocument } from '../hooks/useAllClientDocuments';
import { getClientId } from '../edit/utils/getClientId';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemSelectionMapping } from '../hooks/useItemSelectionMapping';
import { useCombinedFileUpload } from '../hooks/useCombinedFileUpload';
import { useFilesWithAdvice } from '../hooks/useFilesWithAdvice';

type AdviceFilter = 'with-advice' | 'other';

const FILTER_LABELS: Record<AdviceFilter, string> = {
  'with-advice': 'With advice',
  other: 'Other files',
};

const FILTER_TABS: readonly AdviceFilter[] = ['with-advice', 'other'] as const;

export function AdviceStep({ data, setData, filter, searchBar }: StepProps) {
  const clientId = getClientId(data);
  const [documentFilter, setDocumentFilter] = useState<AdviceFilter>('with-advice');
  const theme = useMantineTheme();

  const { documents = [], isLoading } = useAllClientDocuments(
    data.isFamily && data.family
      ? { type: 'family', familyId: data.family.id }
      : { type: 'client', clientId },
  );

  const audioFileIdsWithMeetingNote = new Set(
    documents.flatMap(doc =>
      doc.documentType === 'meeting-note' && doc.dataUploadFileId ? [doc.dataUploadFileId] : [],
    ),
  );

  const files: UploadedFile[] = documents
    .filter(
      doc =>
        doc.documentType !== 'data-export' &&
        doc.documentType !== 'suitability-report' &&
        doc.documentType !== 'meeting-agenda' &&
        doc.file &&
        !(doc.file.fileType?.startsWith('audio/') && audioFileIdsWithMeetingNote.has(doc.id)),
    )
    .map(mapUnifiedDocumentToUploadedFile);

  const allItems = useSelectableUploadItems(isLoading ? [] : files);

  const fileIds = Array.from(new Set(allItems.map(item => item.dataUploadFileId ?? item.id)));
  const { fileIdsWithAdvice, isLoading: isAdviceLoading } = useFilesWithAdvice(fileIds);

  const selectedIds = data.adviceFiles.map(f => f.id);
  const selectedCount = selectedIds.length;

  const familyId = data.isFamily ? data.family?.id : undefined;

  const { isUploading, handleFileUpload, handleOneDriveUpload } = useCombinedFileUpload({
    clientId,
    familyId,
    onUploaded: uploadedFiles => {
      setData(prev => ({
        ...prev,
        adviceFiles: [...prev.adviceFiles, ...uploadedFiles],
      }));
    },
    onLoadingChange: isLoading => {
      setData(prev => ({ ...prev, isLoading }));
    },
  });

  useEffect(() => {
    if (!allItems.length) return;

    setData(prev => {
      let changed = false;

      const nextAdviceFiles = prev.adviceFiles.map(f => {
        const item = allItems.find(i => i.id === f.id);
        const nextName = item?.title ?? 'Untitled file';

        if (f.name !== nextName) {
          changed = true;
          return { ...f, name: nextName };
        }

        return f;
      });

      return changed ? { ...prev, adviceFiles: nextAdviceFiles } : prev;
    });
  }, [allItems, setData]);

  const tabFilteredItems =
    documentFilter === 'with-advice'
      ? allItems.filter(item => fileIdsWithAdvice.has(item.dataUploadFileId ?? item.id))
      : allItems.filter(item => !fileIdsWithAdvice.has(item.dataUploadFileId ?? item.id));

  const filteredItems = useFilteredItems(tabFilteredItems, filter);
  const mapIdsToSelectedFiles = useItemSelectionMapping(allItems);

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title="Select documents that contain your advice / recommendations"
        subTitle="Your advice (taken from meeting notes, recordings, internal notes) will be used to generate the report."
        searchBar={searchBar}
      >
        <Flex align="center" justify="space-between" w="100%" mb="sm" px="xxxs" gap="xxs">
          <Group gap="xs" align="center">
            <SelectedSwitchRow
              selectedCount={selectedCount}
              checked={data.optimiseAdviceReadability}
              onChange={checked =>
                setData(prev => ({
                  ...prev,
                  optimiseAdviceReadability: checked,
                }))
              }
            />

            <Group gap={0}>
              {FILTER_TABS.map(tab => (
                <Text
                  key={tab}
                  size="sm"
                  fw={FONT_WEIGHTS.semibold}
                  c={documentFilter === tab ? 'orange.7' : 'gray.6'}
                  bg={documentFilter === tab ? 'orange.1' : undefined}
                  px="xs"
                  py="xxs"
                  style={{ cursor: 'pointer', borderRadius: theme.radius.md }}
                  onClick={() => setDocumentFilter(tab)}
                >
                  {FILTER_LABELS[tab]}
                </Text>
              ))}
            </Group>
          </Group>

          <UploadPillButton
            loading={isUploading}
            onFilesSelected={handleFileUpload}
            onOneDriveFilesSelected={handleOneDriveUpload}
          />
        </Flex>

        <Box flex={1} mih={0}>
          {isLoading || isAdviceLoading ? (
            <Flex justify="center" align="center" h="100%">
              <Loader data-testid="loader" />
            </Flex>
          ) : (
            <SuitabilitySelectableList
              items={filteredItems.map(f => ({
                id: f.id,
                title: f.title,
                description: f.description,
                file: f.file,
              }))}
              selectMultiple
              selectedItemIds={selectedIds}
              setSelectedItemIds={ids =>
                setData(prev => ({
                  ...prev,
                  adviceFiles: mapIdsToSelectedFiles(ids),
                }))
              }
              highlightedElement={data.highlightedElement}
              emptyView={
                <Text c="gray.6">
                  {documentFilter === 'with-advice' ? 'No advice found' : 'No other files found'}
                </Text>
              }
            />
          )}
        </Box>
      </StepCard>
    </Flex>
  );
}

function mapUnifiedDocumentToUploadedFile(doc: UnifiedDocument): UploadedFile {
  if (!doc.file) {
    throw new Error('Document must have a file');
  }

  return {
    id: doc.id,
    clientId: doc.clientId,
    isFolder: false,
    isResolved: doc.backgroundProcess?.status === 'SUCCESS',
    createdAt: doc.createdAt,
    parentId: undefined,
    backgroundProcess: doc.backgroundProcess,
    dataUploadModel: undefined,
    file: doc.file,
    dataUploadFileId: doc.documentType === 'meeting-note' ? doc.dataUploadFileId : undefined,
    detectedClients: [],
  };
}
